import type { GlyphKind } from "../types";
import { AxonMark } from "./AxonMark";

interface Props {
  kind: GlyphKind;
  className?: string;
}

export function Glyph({ kind, className }: Props) {
  if (kind === "axonmark") {
    return (
      <div className={className} style={{ display: "flex", justifyContent: "center" }}>
        <AxonMark size={44} />
      </div>
    );
  }

  return (
    <svg
      viewBox="0 0 200 44"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      className={className}
    >
      {kind === "waveform" && (
        <>
          <path
            d="M 0 22 Q 12 2, 24 22 T 48 22 T 72 22 T 96 22 T 120 22 T 144 22 T 168 22 T 200 22"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M 0 22 Q 16 36, 32 22 T 64 22 T 96 22 T 128 22 T 160 22 T 200 22"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.45"
          />
          <circle cx="100" cy="22" r="2.5" fill="currentColor" />
          <line
            x1="100"
            y1="4"
            x2="100"
            y2="40"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="2 3"
            opacity="0.5"
          />
        </>
      )}

      {kind === "log" && (
        <>
          <rect x="10" y="6" width="8" height="3" fill="currentColor" opacity="0.85" />
          <rect x="24" y="6" width="120" height="3" fill="currentColor" opacity="0.55" />
          <rect x="10" y="14" width="8" height="3" fill="currentColor" opacity="0.85" />
          <rect x="24" y="14" width="156" height="3" fill="currentColor" opacity="0.55" />
          <rect x="10" y="22" width="8" height="3" fill="currentColor" />
          <rect x="24" y="22" width="92" height="3" fill="currentColor" opacity="0.85" />
          <rect x="10" y="30" width="8" height="3" fill="currentColor" opacity="0.85" />
          <rect x="24" y="30" width="140" height="3" fill="currentColor" opacity="0.55" />
          <rect x="10" y="38" width="8" height="3" fill="currentColor" opacity="0.85" />
          <rect x="24" y="38" width="68" height="3" fill="currentColor" opacity="0.55" />
        </>
      )}

      {kind === "packets" && (
        <>
          <line x1="20" y1="22" x2="180" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.4" />
          <circle cx="20" cy="22" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="100" cy="22" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="180" cy="22" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="55" y="17" width="10" height="10" fill="currentColor" opacity="0.85" />
          <rect x="135" y="17" width="10" height="10" fill="currentColor" opacity="0.55" />
          <path
            d="M 70 22 L 78 22 M 76 19 L 78 22 L 76 25"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M 150 22 L 158 22 M 156 19 L 158 22 L 156 25"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )}

      {kind === "tags" && (
        <>
          <path
            d="M 8 13 L 40 13 L 52 22 L 40 31 L 8 31 Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <circle cx="42" cy="22" r="2.2" fill="currentColor" />
          <path d="M 52 22 L 96 11" stroke="currentColor" strokeWidth="1" opacity="0.45" />
          <path d="M 52 22 L 96 22" stroke="currentColor" strokeWidth="1" opacity="0.45" />
          <path d="M 52 22 L 96 33" stroke="currentColor" strokeWidth="1" opacity="0.45" />
          <circle cx="100" cy="11" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="100" cy="22" r="4" fill="currentColor" />
          <circle cx="100" cy="33" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="112" y="9.5" width="60" height="3" fill="currentColor" opacity="0.55" />
          <rect x="112" y="20.5" width="80" height="3" fill="currentColor" opacity="0.85" />
          <rect x="112" y="31.5" width="46" height="3" fill="currentColor" opacity="0.55" />
        </>
      )}

      {kind === "python" && (
        <>
          <rect x="8" y="11" width="26" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M 18 17 L 18 27 L 27 22 Z" fill="currentColor" />
          <path d="M 46 14 L 51 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
          <rect x="57" y="12.5" width="58" height="3" fill="currentColor" opacity="0.55" />
          <path d="M 46 22 L 51 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
          <rect x="57" y="20.5" width="121" height="3" fill="currentColor" opacity="0.85" />
          <path d="M 46 30 L 51 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
          <rect x="57" y="28.5" width="86" height="3" fill="currentColor" opacity="0.55" />
        </>
      )}

      {kind === "network" && (
        <>
          <path d="M 26 12 L 100 22 L 26 32" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.45" />
          <path d="M 100 22 L 174 12 M 100 22 L 174 32" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.45" />
          <path d="M 26 12 L 26 32 M 174 12 L 174 32" stroke="currentColor" strokeWidth="1" opacity="0.45" />
          <circle cx="26" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="26" cy="32" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="174" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="174" cy="32" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="100" cy="22" r="5.5" fill="currentColor" />
        </>
      )}

      {kind === "diagram" && (
        <>
          <path d="M 56 22 L 93 22 M 107 22 L 140 22" stroke="currentColor" strokeWidth="1" opacity="0.5" />
          <rect x="24" y="13" width="32" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M 29 19 L 51 19 M 29 25 L 45 25" stroke="currentColor" strokeWidth="1" opacity="0.4" />
          <circle cx="100" cy="22" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M 93 22 L 107 22 M 100 15 L 100 29" stroke="currentColor" strokeWidth="1.2" opacity="0.85" />
          <rect x="140" y="13" width="32" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M 145 19 L 167 19 M 145 25 L 161 25" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        </>
      )}
    </svg>
  );
}
